/**
 * Per-market examples for the demo UI.
 *
 * Presentation only — placeholders, sample queries, currency options. It exists
 * because a demo shown to a South African prospect with "Harare" in the address
 * field and a R2 000 example query undermines the thing it is demonstrating: the
 * details are wrong in exactly the way a local notices first, and the example
 * query returns nothing because R2 000 is below the market's own floor.
 *
 * The market itself is NOT guessed here — it comes from `GET /v1/usage`, which
 * returns the tenant's configured markets and currency. This table only decides
 * what to *show* for a market we were told about.
 *
 * The price bands mirror `search-service/scripts/demo/places.ts`, so an example
 * query always has stock behind it. If you change the seeder's bands, change
 * these — an example that returns zero results is worse than no example.
 */
export interface MarketExamples {
  currency: string;
  /** Offered in the currency selector, the tenant's own first. */
  currencies: string[];
  city: string;
  suburb: string;
  street: string;
  title: string;
  body: string;
  amenities: string;
  /** Escalating: plain, then a budget, then an amenity, then pure natural language. */
  queries: string[];
  /** The greyed-out prompt in the search box. Carries a market-plausible budget. */
  searchPlaceholder: string;
  /** A plausible monthly rent for this market, for the price field. */
  price: string;
}

const ZW: MarketExamples = {
  currency: 'USD',
  currencies: ['USD', 'ZWG', 'ZAR'],
  city: 'Harare',
  suburb: 'Avondale',
  street: '12 Acacia Road',
  title: '3 bedroom house to rent in Avondale',
  body: 'Spacious family home with a borehole, solar backup and a walled garden. Close to schools and the CBD.',
  amenities: 'borehole, solar power, pet friendly',
  queries: [
    'two bedroom apartment',
    '3 bedroom house under 1200 a month',
    'somewhere pet friendly with a garden',
    'furnished flat with fibre and backup power',
    'two bed within 30 minutes of Msasa',
  ],
  searchPlaceholder: 'e.g. two bed in Avondale within 30 minutes of Msasa',
  price: '1200',
};

const ZA: MarketExamples = {
  currency: 'ZAR',
  currencies: ['ZAR', 'USD'],
  city: 'Cape Town',
  suburb: 'Newlands',
  street: '12 Protea Road',
  title: '3 bedroom house to rent in Newlands',
  body: 'Spacious family home with a pool, solar backup and a walled garden. Close to schools and the M3.',
  amenities: 'swimming pool, solar power, pet friendly',
  queries: [
    'two bedroom apartment',
    // Above the ZA floor of R4 500 and below the median of ~R12 000, so it
    // returns a real slice rather than everything or nothing.
    '3 bedroom house under 15000 a month',
    'somewhere pet friendly with a garden',
    'furnished flat with fibre and backup power',
    '3 bedroom within 40 minutes of University of Cape Town',
  ],
  searchPlaceholder: 'e.g. two bed in Newlands within 40 minutes of University of Cape Town',
  price: '12000',
};

const BY_MARKET: Record<string, MarketExamples> = { zw: ZW, za: ZA };

/**
 * Examples for a tenant's market.
 *
 * Falls back to Zimbabwe for an unknown or missing market rather than showing
 * empty fields — the placeholders still communicate the SHAPE of an address,
 * which is most of their job. `currency` from the API wins over the table's,
 * because the tenant's own setting is authoritative and this file is a mirror.
 */
export function examplesFor(markets?: string[], currency?: string | null): MarketExamples {
  const key = markets?.[0]?.toLowerCase();
  const base = (key && BY_MARKET[key]) || ZW;
  if (!currency || currency === base.currency) return base;
  return {
    ...base,
    currency,
    currencies: [currency, ...base.currencies.filter((c) => c !== currency)],
  };
}
